import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Select, Input } from 'semantic-ui-react';
import { fetcher } from '@weezevent/weezjs-core';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

/**
 * Display a row of Partner
 * @param organizationId
 * @param eventId
 * @param setForm
 * @param form
 * @param inventory
 * @param row
 * @param id
 * @returns {*}
 * @constructor
 */
export function RowManager({ organizationId, eventId, setForm, form, id, inventories, row }) {
    const { t } = useTranslation();
    const [zones, setZones] = React.useState(null);
    const [access, setAccess] = React.useState(null);
    const [rows, setRows] = React.useState(null);
    const [seats, setSeats] = React.useState(null);
    const [inventorySelected, setInventorySelected] = React.useState(null);
    const [zoneSelected, setZoneSelected] = React.useState(null);
    const [accessSelected, setAccessSelected] = React.useState(null);
    const [rowSelected, setRowSelected] = React.useState(null);
    const [seatSelected, setSeatSelected] = React.useState(null);
    const [name, setName] = React.useState(null);

    const toast = React.useContext(ToastContext);

    const mapToSelect = ({ uuid, name }) => ({ key: uuid, text: name, value: uuid });

    // ON LOAD
    React.useEffect(() => {
        if (row.inventory) {
            setInventorySelected(row.inventory);
        }

        if (row.seat) {
            setSeatSelected(row.seat);
        }

        if (row.zone) {
            setZoneSelected(row.zone);
        }

        if (row.acces) {
            setAccessSelected(row.acces);
        }

        if (row.row) {
            setRowSelected(row.row);
        }

        if (row.nameForm) {
            setName(row.nameForm);
        }
    }, []);

    function deleteFromData(property, data) {
        let indice = data.findIndex(element => {
            return element.value === property;
        });
        data.splice(indice, 1);
        return data;
    }

    React.useEffect(() => {
        if (inventorySelected) {
            let zones = fetcher.get(`/ticket/organizations/${organizationId}/events/${eventId}/partners/inventories/${inventorySelected}/zones`);

            zones
                .then(zones => {
                    let tmpZones = zones.response.map(mapToSelect);
                    if (!tmpZones || !tmpZones.length) {
                        toast.error(t('ticket.partner.form.inventory_empty'));
                        setInventorySelected(null);
                    } else {
                        setZones(tmpZones);
                    }
                })
                .catch(e => {
                    toast.error(t(`ticket.partner.form.zone_error`));
                });
        }
    }, [inventorySelected]);

    React.useEffect(() => {
        if (zoneSelected) {
            let access = fetcher.get(`/ticket/organizations/${organizationId}/events/${eventId}/partners/inventories/${inventorySelected}/zones/${zoneSelected}/accesses`);
            const accessToSelect = ({ access }) => ({ key: access, text: access, value: access });
            access
                .then(access => {
                    let tmpAccess = access.response.map(accessToSelect);
                    if (!tmpAccess || !tmpAccess.length) {
                        toast.error(t('ticket.partner.form.zone_empty'));
                        let tmpZones = deleteFromData(zoneSelected, zones);
                        setZones(tmpZones);
                        setZoneSelected(null);
                    } else {
                        setAccess(tmpAccess);
                    }
                })
                .catch(e => {
                    toast.error(t(`ticket.partner.form.access_error`));
                });
        }
    }, [zoneSelected]);

    React.useEffect(() => {
        if (accessSelected) {
            let rows = fetcher.get(
                `/ticket/organizations/${organizationId}/events/${eventId}/partners/inventories/${inventorySelected}/zones/${zoneSelected}/accesses/${accessSelected}/rows`
            );
            const rowToSelect = ({ row }) => ({ key: row, text: row, value: row });
            rows.then(rows => {
                let tmpRows = rows.response.map(rowToSelect);
                if (!tmpRows || !tmpRows.length) {
                    toast.error(t('ticket.partner.form.access_empty'));
                    let tmpAccess = deleteFromData(accessSelected, access);
                    setAccess(tmpAccess);
                    setAccessSelected(null);
                } else {
                    setRows(tmpRows);
                }
            }).catch(e => {
                toast.error(t(`ticket.partner.form.row_error`));
            });
        }
    }, [accessSelected]);

    React.useEffect(() => {
        if (rowSelected) {
            let seats = fetcher.get(
                `/ticket/organizations/${organizationId}/events/${eventId}/partners/inventories/${inventorySelected}/zones/${zoneSelected}/accesses/${accessSelected}/rows/${rowSelected}/seats`
            );
            const seatToSelect = seat => ({ key: seat.uuid, text: seat.access + '-' + seat.row + '-' + seat.seat, value: seat.uuid });
            seats
                .then(seats => {
                    let tmpSeats = seats.response.map(seatToSelect);
                    if (!tmpSeats || !tmpSeats.length) {
                        toast.error(t('ticket.partner.form.row_empty'));
                        let tmpRows = deleteFromData(rowSelected, rows);
                        setRows(tmpRows);
                        setRowSelected(null);
                    } else {
                        setSeats(tmpSeats);
                    }
                })
                .catch(e => {
                    toast.error(t(`ticket.partner.form.seat_error`));
                });
        }
    }, [rowSelected]);

    React.useEffect(() => {
        if (seatSelected) {
            let data = {
                seat: seatSelected,
                row: rowSelected,
                access: accessSelected,
                zone: zoneSelected,
                inventory: inventorySelected,
                event: eventId,
                name: name
            };

            let newForm = [...form];
            newForm.splice(id, 1, data);
            setForm(newForm);
        }
    }, [seatSelected]);

    React.useEffect(() => {
        let timer = setTimeout(() => {
            let data = {
                seat: seatSelected,
                row: rowSelected,
                access: accessSelected,
                zone: zoneSelected,
                inventory: inventorySelected,
                event: eventId,
                name: name
            };
            let newForm = [...form];
            newForm.splice(id, 1, data);
            setForm(newForm);
        }, 300);
        return () => clearTimeout(timer);
    }, [name]);

    return (
        <Grid.Row columns={6} stretched>
            {/* INVENTORY*/}
            <Grid.Column stretched>
                <Select
                    fluid
                    options={inventories}
                    value={inventorySelected}
                    onChange={(inventory, { value }) => {
                        setZoneSelected(null);
                        setAccessSelected(null);
                        setRowSelected(null);
                        setSeatSelected(null);
                        setZones(null);
                        setAccess(null);
                        setRows(null);
                        setSeats(null);
                        setInventorySelected(value);
                    }}
                />
            </Grid.Column>

            {/* ZONE*/}
            <Grid.Column stretched>
                <Select
                    fluid
                    options={zones}
                    value={zoneSelected}
                    onChange={(zones, { value }) => {
                        setAccessSelected(null);
                        setRowSelected(null);
                        setSeatSelected(null);
                        setAccess(null);
                        setRows(null);
                        setSeats(null);
                        setZoneSelected(value);
                    }}
                    loading={inventorySelected ? (!zones ? true : false) : false}
                    disabled={!zones}
                />
            </Grid.Column>

            {/* ACCESS*/}
            <Grid.Column stretched>
                <Select
                    fluid
                    options={access}
                    value={accessSelected}
                    onChange={(access, { value }) => {
                        setRowSelected(null);
                        setSeatSelected(null);
                        setRows(null);
                        setSeats(null);
                        setAccessSelected(value);
                    }}
                    loading={zoneSelected ? (!access ? true : false) : false}
                    disabled={!access}
                />
            </Grid.Column>

            {/* ROW*/}
            <Grid.Column stretched>
                <Select
                    fluid
                    options={rows}
                    value={rowSelected}
                    onChange={(rows, { value }) => {
                        setSeatSelected(null);
                        setSeats(null);
                        setRowSelected(value);
                    }}
                    loading={accessSelected ? (!rows ? true : false) : false}
                    disabled={!rows}
                />
            </Grid.Column>

            {/* SEAT*/}
            <Grid.Column stretched>
                <Select
                    fluid
                    options={seats}
                    value={seatSelected}
                    onChange={(seat, { value }) => {
                        setSeatSelected(value);
                    }}
                    loading={rowSelected ? (!zones ? true : false) : false}
                    disabled={!seats}
                />
            </Grid.Column>

            {/* NAME*/}
            <Grid.Column stretched>
                <Input value={name} disabled={!seatSelected} onChange={event => setName(event.target.value)} />
            </Grid.Column>
        </Grid.Row>
    );
}

RowManager.propTypes = {
    organizationId: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired,
    setForm: PropTypes.func.isRequired,
    form: PropTypes.array.isRequired,
    row: PropTypes.array,
    nameForm: PropTypes.string,
    id: PropTypes.number,
    inventories: PropTypes.array
};
